// app/src/Board.jsx — Crew Board (news feed).
//
// Server-authoritative. On a paid workplace the server returns each post in the
// reader's language; free shows the original. Anyone can post; managers can pin
// and require acknowledgement. Acknowledge + emoji reactions are wired here.

const REACTIONS = ['👍', '❤️', '🙏', '✅'];

function PostCard({ post, avatarUrl, authorRole, memberCount, onAck, onReact }) {
  const roleLabel = authorRole === 'owner'
    ? t('role.owner', 'OWNER')
    : (authorRole === 'manager' ? t('role.manager', 'MANAGER') : null);
  return (
    <article style={{
      background: 'var(--surface)', border: '1px solid var(--line)',
      borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)',
      padding: '13px 14px', margin: '0 14px 12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
        <Avatar name={post.authorName} url={avatarUrl} size={30} />
        <div style={{ flex: 1, lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.authorName || t('common.member', 'Member')}</span>
            {roleLabel && (
              <span style={{ background: 'var(--primary-soft)', color: 'var(--primary)', fontSize: 8.5, fontWeight: 800, padding: '2px 6px', borderRadius: 5, letterSpacing: '.4px', flex: 'none' }}>{roleLabel}</span>
            )}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--sub)' }}>{post.timeLabel || ''}</div>
        </div>
        {post.pinned && (
          <span style={{ background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999, letterSpacing: '.4px' }}>{t('board.pinned', 'PINNED')}</span>
        )}
      </div>

      {post.body && <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{post.body}</div>}
      {post.attachments && post.attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: post.body ? 10 : 0 }}>
          {post.attachments.map((a, i) => <PhotoTile key={i} a={a} n={post.attachments.length} />)}
        </div>
      )}
      {post.translatedFrom && (
        <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--sub)' }}>🌐 {t('board.translatedFrom', 'Auto-translated from {lang}', { lang: post.translatedFrom })}</div>
      )}

      {/* Reactions — tap to toggle; reacted chips fill periwinkle and show the count. */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {REACTIONS.map((e) => {
          const n = (post.reactions && post.reactions[e]) || 0;
          const on = n > 0;
          return (
            <button key={e} onClick={() => onReact(post.postId, e)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
              border: `1px solid ${on ? 'var(--primary)' : 'var(--line)'}`,
              background: on ? 'var(--primary-soft)' : 'var(--surface-2)',
              borderRadius: 999, padding: '4px 10px', fontSize: 12.5, lineHeight: 1,
            }}>
              <span aria-hidden="true">{e}</span>
              {on && <span style={{ fontWeight: 800, fontSize: 11, color: 'var(--primary)' }}>{n}</span>}
            </button>
          );
        })}
      </div>

      {/* Acknowledgement */}
      {post.requiresAck && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, paddingTop: 11, marginTop: 10, borderTop: '1px solid var(--line)' }}>
          <span style={{ fontSize: 10.5, color: 'var(--sub)', flex: 1 }}>{memberCount
            ? t('board.readByOf', 'Read by {n} of {total}', { n: post.ackCount || 0, total: memberCount })
            : t('board.readBy', 'Read by {n}', { n: post.ackCount || 0 })}</span>
          {post.ackedByMe ? (
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ok)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>✓ {t('board.acknowledged', 'Acknowledged')}</span>
          ) : (
            <button onClick={() => onAck(post.postId)} style={{
              background: 'var(--primary)', color: 'var(--primary-ink)', border: 'none', borderRadius: 999,
              padding: '7px 15px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
            }}>{t('board.acknowledge', 'Acknowledge')}</button>
          )}
        </div>
      )}
    </article>
  );
}

function timeLabel(ts) {
  if (!ts) return '';
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return t('time.justNow', 'just now');
  if (m < 60) return t('time.minutesAgo', '{n}m ago', { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('time.hoursAgo', '{n}h ago', { n: h });
  return t('time.daysAgo', '{n}d ago', { n: Math.floor(h / 24) });
}

function BoardScreen() {
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [] };
  const myWp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};
  const myLang = myWp.preferredLang || 'en';
  const myName = myWp.displayName || (me.profile && me.profile.name) || t('common.member', 'Member');
  const isManager = myWp.role === 'owner' || myWp.role === 'manager';

  const [posts, setPosts] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [filter, setFilter] = React.useState('all'); // 'all' | 'pinned' | 'tasks'
  const [draft, setDraft] = React.useState('');
  const [pin, setPin] = React.useState(false);
  const [reqAck, setReqAck] = React.useState(false);
  const [repeat, setRepeat] = React.useState(null);      // { frequency, weekday|dayOfMonth } | null
  const [repeatOpen, setRepeatOpen] = React.useState(false);
  const [manageOpen, setManageOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState([]); // [{ file, url, contentType, w, h }] — up to 3 photos
  const fileRef = React.useRef(null);

  // Pick up to 3 photos; load each to grab its natural dimensions for the tile.
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (!files.length) return;
    const take = files.slice(0, Math.max(0, 3 - pending.length));
    const loaded = await Promise.all(take.map((file) => new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ file, url, contentType: file.type, w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ file, url, contentType: file.type, w: null, h: null });
      img.src = url;
    })));
    setPending((p) => [...p, ...loaded].slice(0, 3));
  };
  const removePending = (i) => setPending((p) => { const x = p[i]; if (x) URL.revokeObjectURL(x.url); return p.filter((_, idx) => idx !== i); });

  const load = React.useCallback(async () => {
    if (!wid) { setPosts([]); return; }
    try {
      const res = await window.crew.api(`/workplaces/${wid}/posts?lang=${encodeURIComponent(myLang)}`, { workplaceId: wid });
      setPosts(((res && res.posts) || []).map((p) => ({ ...p, timeLabel: timeLabel(p.createdAt) })));
    } catch (e) { console.warn('[board] load failed', e && e.message); setPosts([]); }
  }, [wid, myLang]);

  React.useEffect(() => { load(); }, [load]);

  // Member directory → author avatars on posts (by authorId).
  React.useEffect(() => {
    if (!wid) return;
    window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid })
      .then((r) => setMembers((r && r.members) || [])).catch(() => {});
  }, [wid]);
  const memberOf = (id) => members.find((x) => x.userId === id) || null;
  const avatarOf = (id) => { const m = memberOf(id); return (m && m.avatarUrl) || null; };
  const roleOf = (id) => { const m = memberOf(id); return (m && m.role) || null; };

  const submit = async () => {
    const text = draft.trim();
    if (busy) return;
    // Recurring task: text-only template; occurrences materialise on board-read.
    if (repeat) {
      if (!text) return;
      setBusy(true);
      try {
        let tz = 'UTC';
        try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* keep UTC */ }
        await window.crew.api(`/workplaces/${wid}/recurring-tasks`, {
          method: 'POST', workplaceId: wid,
          body: { body: text, authorName: myName, sourceLang: myLang, pinned: pin, timezone: tz, ...repeat },
        });
        setDraft(''); setPin(false); setReqAck(false); setRepeat(null);
        // Recurring tasks are text-only — release any staged photos so they aren't
        // leaked (objectURL) or silently carried onto the next ordinary post.
        pending.forEach((p) => URL.revokeObjectURL(p.url));
        setPending([]);
        await load();
      } catch (e) { console.warn('[board] recurring create failed', e && e.message); }
      finally { setBusy(false); }
      return;
    }
    if (!text && pending.length === 0) return;
    setBusy(true);
    try {
      // Upload photos straight to S3 via presigned PUTs, then post the keys.
      let attachments = [];
      if (pending.length) {
        const pres = await window.crew.api(`/workplaces/${wid}/posts/attachments/presign`, {
          method: 'POST', workplaceId: wid, body: { items: pending.map((p) => ({ contentType: p.contentType })) },
        });
        const slots = (pres && pres.attachments) || [];
        await Promise.all(slots.map((slot, i) => fetch(slot.putUrl, { method: 'PUT', headers: { 'content-type': slot.contentType }, body: pending[i].file })));
        attachments = slots.map((slot, i) => ({ key: slot.key, contentType: slot.contentType, w: pending[i].w, h: pending[i].h }));
      }
      await window.crew.api(`/workplaces/${wid}/posts`, {
        method: 'POST', workplaceId: wid,
        body: { body: text, authorName: myName, sourceLang: myLang, pinned: pin, requiresAck: reqAck, attachments },
      });
      setDraft(''); setPin(false); setReqAck(false);
      pending.forEach((p) => URL.revokeObjectURL(p.url));
      setPending([]);
      await load();
    } catch (e) { console.warn('[board] post failed', e && e.message); } finally { setBusy(false); }
  };

  const ack = async (pid) => {
    try { await window.crew.api(`/workplaces/${wid}/posts/${pid}/ack`, { method: 'POST', workplaceId: wid, body: {} }); await load(); }
    catch (e) { console.warn('[board] ack failed', e && e.message); }
  };
  const react = async (pid, emoji) => {
    try { await window.crew.api(`/workplaces/${wid}/posts/${pid}/reactions/${encodeURIComponent(emoji)}`, { method: 'PUT', workplaceId: wid, body: {} }); await load(); }
    catch (e) { console.warn('[board] react failed', e && e.message); }
  };

  const title = myWp.name || t('board.title', 'Crew Board');
  const all = posts || [];
  // "Tasks" = posts that require acknowledgement. The count/bell/app-badge reflect
  // only what THIS user hasn't acked yet (ackedByMe comes from the server), so it
  // returns to 0 — and clears the OS badge — once they've acknowledged everything.
  const taskCount = all.filter((p) => p.requiresAck && !p.ackedByMe).length;
  const shown = filter === 'pinned' ? all.filter((p) => p.pinned)
    : filter === 'tasks' ? all.filter((p) => p.requiresAck)
    : all;
  const FILTERS = [
    { key: 'all', label: t('board.filterAll', 'All') },
    { key: 'pinned', label: t('board.filterPinned', 'Pinned') },
    { key: 'tasks', label: t('board.filterTasks', 'Tasks') },
  ];

  return (
    <KShell tab="board">
      <KHeader title={title} right={<NotifBell count={taskCount} onClick={() => setFilter('tasks')} />} />

      {/* Filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px 2px' }}>
        {FILTERS.map((f) => {
          const on = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              border: on ? 'none' : '1px solid var(--line-on)',
              background: on ? 'var(--primary)' : 'rgba(255,255,255,.07)',
              color: on ? 'var(--primary-ink)' : 'var(--on-soft)',
              fontSize: 11.5, fontWeight: on ? 700 : 600, padding: '6px 13px',
              borderRadius: 999, cursor: 'pointer',
            }}>{f.label}{f.key === 'tasks' && taskCount > 0 ? ` · ${taskCount}` : ''}</button>
          );
        })}
        {isManager && (
          <button onClick={() => setManageOpen(true)} title={t('board.recurring', 'Recurring tasks')} style={{
            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--line-on)',
            background: 'rgba(255,255,255,.07)', color: 'var(--on-soft)', fontSize: 11.5, fontWeight: 600, padding: '6px 11px', borderRadius: 999, cursor: 'pointer',
          }}>🔁 {t('board.recurring', 'Recurring')}</button>
        )}
      </div>

      {/* Compose */}
      <div style={{ padding: '12px 14px 4px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 10 }}>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
            placeholder={t('board.compose', 'Write a post')}
            style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontFamily: 'var(--font-body)', boxSizing: 'border-box' }} />
          {pending.length > 0 && (
            <div style={{ display: 'flex', gap: 8, padding: '4px 0 8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {pending.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={p.url} alt="" style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', display: 'block' }} />
                  <button onClick={() => removePending(i)} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(20,12,8,.7)', color: '#fff', fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0 }}>×</button>
                </div>
              ))}
              <span style={{ fontSize: 10, color: 'var(--sub)' }}>{pending.length}/3 · {t('board.keptDays', 'kept 30 days')}</span>
            </div>
          )}
          {repeat && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0 8px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: 999, padding: '5px 11px', fontSize: 11.5, fontWeight: 700 }}>
                🔁 {t('board.repeatsEvery', 'Repeats')} {cadenceLabel(repeat)}
                <button onClick={() => setRepeat(null)} title={t('rt.dontRepeat', "Don't repeat")} style={{ border: 'none', background: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--sub)' }}>{t('board.repeatHint', 'Posts as an acknowledgement task each cycle')}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickPhotos} style={{ display: 'none' }} />
            {!repeat && (
              <button onClick={() => fileRef.current && fileRef.current.click()} disabled={pending.length >= 3} title={t('board.addPhoto', 'Add photo')}
                style={{ border: '1px solid var(--line)', background: 'var(--surface-2)', borderRadius: 999, width: 34, height: 34, flex: 'none', fontSize: 16, cursor: pending.length >= 3 ? 'default' : 'pointer', color: pending.length >= 3 ? 'var(--sub)' : 'var(--primary)', opacity: pending.length >= 3 ? 0.5 : 1, padding: 0 }}>🖼</button>
            )}
            <MicButton lang={myLang} disabled={busy} onText={(text) => setDraft((d) => (d ? d + ' ' : '') + text)} />
            {isManager && (
              <React.Fragment>
                <label style={{ fontSize: 11.5, color: 'var(--sub)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} /> {t('board.pin', 'Pin')}
                </label>
                {!repeat && (
                  <label style={{ fontSize: 11.5, color: 'var(--sub)', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input type="checkbox" checked={reqAck} onChange={(e) => setReqAck(e.target.checked)} /> {t('board.requireAck', 'Require ack')}
                  </label>
                )}
                <button onClick={() => setRepeatOpen(true)} title={t('board.repeat', 'Repeat')} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid var(--line)',
                  background: repeat ? 'var(--primary-soft)' : 'var(--surface-2)', color: repeat ? 'var(--primary)' : 'var(--sub)',
                  borderRadius: 999, padding: '5px 10px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                }}>🔁 {t('board.repeat', 'Repeat')}</button>
              </React.Fragment>
            )}
            <div style={{ flex: 1 }} />
            {(() => {
              const canPost = !busy && (repeat ? !!draft.trim() : (!!draft.trim() || pending.length > 0));
              return (
                <button onClick={submit} disabled={!canPost} style={{
                  border: 'none', borderRadius: 999, padding: '7px 16px', cursor: canPost ? 'pointer' : 'default',
                  background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 13, opacity: canPost ? 1 : 0.5,
                }}>{busy ? '…' : repeat ? t('rt.addTask', 'Add task') : t('common.post', 'Post')}</button>
              );
            })()}
          </div>
        </div>
      </div>

      <div style={{ padding: '8px 0 6px' }}>
        {posts == null && <div style={{ padding: 20, color: 'var(--on-dim)' }}>…</div>}
        {posts != null && all.length === 0 && (
          <div style={{ padding: 24, color: 'var(--on-soft)', textAlign: 'center' }}>{t('board.empty', 'No posts yet.')}</div>
        )}
        {posts != null && all.length > 0 && shown.length === 0 && (
          <div style={{ padding: 24, color: 'var(--on-soft)', textAlign: 'center' }}>{filter === 'pinned'
            ? t('board.emptyPinned', 'Nothing pinned.')
            : t('board.emptyTasks', 'No tasks need acknowledgement.')}</div>
        )}
        {shown.map((p) => <PostCard key={p.postId} post={p} avatarUrl={avatarOf(p.authorId)} authorRole={roleOf(p.authorId)} memberCount={members.length} onAck={ack} onReact={react} />)}
      </div>

      {repeatOpen && (
        <RepeatPicker
          initial={repeat}
          onSet={(r) => { setRepeat(r); setRepeatOpen(false); }}
          onClear={repeat ? () => { setRepeat(null); setRepeatOpen(false); } : null}
          onClose={() => setRepeatOpen(false)}
        />
      )}
      {manageOpen && <RecurringManager wid={wid} onClose={() => { setManageOpen(false); load(); }} />}
    </KShell>
  );
}

// ── Recurring tasks ──────────────────────────────────────────────────────────
const WEEKDAYS = () => [t('day.mon', 'Mon'), t('day.tue', 'Tue'), t('day.wed', 'Wed'), t('day.thu', 'Thu'), t('day.fri', 'Fri'), t('day.sat', 'Sat'), t('day.sun', 'Sun')];
const RT_FREQS = () => [
  { key: 'weekly', label: t('rt.weekly', 'Weekly') },
  { key: 'monthly', label: t('rt.monthly', 'Monthly') },
  { key: 'quarterly', label: t('rt.quarterly', 'Every 3 months') },
  { key: 'semiannual', label: t('rt.semiannual', 'Every 6 months') },
];
function cadenceLabel(rule) {
  if (!rule) return '';
  const f = (RT_FREQS().find((x) => x.key === rule.frequency) || {}).label || rule.frequency;
  if (rule.frequency === 'weekly') return `${f} · ${WEEKDAYS()[(rule.weekday || 1) - 1]}`;
  return `${f} · ${rule.dayOfMonth === 31 ? t('rt.lastDayShort', 'last day') : t('rt.dayN', 'day {d}', { d: rule.dayOfMonth || 1 })}`;
}

function RtSheet({ children, title, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,8,50,.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 430, background: 'var(--surface)', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 20, maxHeight: '85%', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)', marginBottom: 14 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

// Pick a repeat cadence for a task. Calls onSet({ frequency, weekday|dayOfMonth }).
function RepeatPicker({ initial, onSet, onClear, onClose }) {
  const [frequency, setFrequency] = React.useState((initial && initial.frequency) || 'weekly');
  const [weekday, setWeekday] = React.useState((initial && initial.weekday) || 1);
  const [dom, setDom] = React.useState((initial && initial.dayOfMonth) || 1);
  const pillBtn = (on) => ({ padding: '8px 12px', borderRadius: 999, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, border: on ? 'none' : '1px solid var(--line)', background: on ? 'var(--primary)' : 'var(--surface)', color: on ? 'var(--primary-ink)' : 'var(--ink)' });
  return (
    <RtSheet title={t('rt.repeatTitle', 'Repeat this task')} onClose={onClose}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 8 }}>{t('rt.howOften', 'How often')}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {RT_FREQS().map((f) => <button key={f.key} onClick={() => setFrequency(f.key)} style={pillBtn(frequency === f.key)}>{f.label}</button>)}
      </div>
      {frequency === 'weekly' ? (
        <React.Fragment>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 8 }}>{t('rt.onDay', 'On')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
            {WEEKDAYS().map((d, i) => <button key={i} onClick={() => setWeekday(i + 1)} style={pillBtn(weekday === i + 1)}>{d}</button>)}
          </div>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sub)', marginBottom: 8 }}>{t('rt.dayOfMonth', 'Day of the month')}</div>
          <select value={dom} onChange={(e) => setDom(Number(e.target.value))} style={{ width: '100%', padding: '11px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 15, marginBottom: 6, boxSizing: 'border-box' }}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d === 31 ? t('rt.lastDay', '31 / last day') : d}</option>)}
          </select>
          <div style={{ fontSize: 10.5, color: 'var(--sub)', marginBottom: 16 }}>{t('rt.shortMonthNote', 'Shorter months fall back to their last day.')}</div>
        </React.Fragment>
      )}
      <button onClick={() => onSet(frequency === 'weekly' ? { frequency, weekday } : { frequency, dayOfMonth: dom })} style={{
        width: '100%', padding: 13, border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15, cursor: 'pointer',
      }}>{t('rt.setRepeat', 'Set repeat')}</button>
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        {onClear && <button onClick={onClear} style={{ flex: 1, padding: 10, border: 'none', background: 'none', color: 'var(--danger)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('rt.dontRepeat', "Don't repeat")}</button>}
        <button onClick={onClose} style={{ flex: 1, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </RtSheet>
  );
}

// Manager view of existing recurring tasks, with a Stop action.
function RecurringManager({ wid, onClose }) {
  const [list, setList] = React.useState(null);
  const load = React.useCallback(() => {
    window.crew.api(`/workplaces/${wid}/recurring-tasks`, { workplaceId: wid })
      .then((r) => setList((r && r.tasks) || [])).catch(() => setList([]));
  }, [wid]);
  React.useEffect(() => { load(); }, [load]);
  const stop = async (tid) => {
    try { await window.crew.api(`/workplaces/${wid}/recurring-tasks/${tid}`, { method: 'DELETE', workplaceId: wid }); load(); }
    catch (e) { console.warn('[rt] stop failed', e && e.message); }
  };
  const fmtNext = (ms, tz) => { try { return new Date(ms).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz || undefined }); } catch { return ''; } };
  return (
    <RtSheet title={t('rt.manageTitle', 'Recurring tasks')} onClose={onClose}>
      {list == null && <div style={{ color: 'var(--sub)', padding: 12 }}>…</div>}
      {list != null && list.length === 0 && <div style={{ color: 'var(--sub)', textAlign: 'center', padding: 20, fontSize: 13 }}>{t('rt.none', 'No recurring tasks yet. Write a post, tap Repeat, and post it.')}</div>}
      {(list || []).map((tk) => (
        <div key={tk.taskId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderTop: '1px solid var(--line)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tk.body}</div>
            <div style={{ fontSize: 11.5, color: 'var(--sub)', marginTop: 2 }}>🔁 {cadenceLabel(tk)} · {t('rt.next', 'next {date}', { date: fmtNext(tk.nextDue, tk.timezone) })}</div>
          </div>
          <button onClick={() => stop(tk.taskId)} style={{ flex: 'none', padding: '7px 13px', borderRadius: 999, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--danger)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{t('rt.stop', 'Stop')}</button>
        </div>
      ))}
      <button onClick={onClose} style={{ width: '100%', marginTop: 16, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.close', 'Close')}</button>
    </RtSheet>
  );
}

Object.assign(window, { BoardScreen, PostCard });
